import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import * as yup from "yup";
import styles from "../../common/forms/forms.module.css";
import { FormHeader } from "../../common/forms/FormHeader";
import { FormButtons } from "../../common/forms/FormButtons";
import { EntityFormProps } from "../../common/entity/EntityInterfaces";
import {
  DataSourceType,
  DataSourceTypeFormValues,
  DataSourceUpdateModesTranslator,
  FileExcludePattern,
} from "../../api/DataSource";
import { BridgesVirtualizedSelectForm } from "../../common/forms/EntityForms/formsVirtualized/BridgeVirtualizedSelectForm";
import { FormFieldLayout } from "../../common/formfields/FormFieldLayouts";
import { useEntityDetail } from "../../api/BaseEntityApi";
import { Bridge, bridgeConstants } from "../../api/Bridges";
import { InputFormField } from "../../common/formfields/InputFormField";
import { SelectFormField } from "../../common/formfields/SelectFormField";
import { DataFormatsVirtualizedSelectForm } from "../../common/forms/EntityForms/formsVirtualized/DataFormatsVirtualizedSelectForm";
import { CustomImportsVirtualizedSelectForm } from "../../common/forms/EntityForms/formsVirtualized/CustomImportVirtualizedSelectForm";
import { useEffect, useMemo, useState } from "react";
import { ToggleButtonComponent } from "../../ViewerUIElements/ToggleButtonComponent";
import { LoadingWrapper } from "../../common/LoadingWrapper";
import { FileBrowserFormField } from "../../common/formfields/FileBrowserFormField";
import { Alert } from "../../common/overlays/Alert/Alert";
import { useHistory } from "react-router-dom";
import {
  BridgeContentRow,
  MetaDataTemplateInformation,
  CutoffDateInformation,
  DataSourceFormValues,
  useDatasetForm,
  DatasetTemplateInformation,
} from "../DataSources/DataSourceForm";
import { LucideIcon } from "../../common/icon/LucideIcon";
import { DataFormat, dataFormatsConstants } from "../../api/DataFormats";
import { SingleDatePickerFormField } from "../../common/formfields/DatePickerFormField/SingleDatePickerFormField";
import { customValuesListToCustomFieldDict } from "../../Customization/common/CustomFieldConverter";
import { Dataset } from "../../api/Datasets";

interface IconNMRDataSourceFormValues extends Omit<DataSourceFormValues, "intervalInSeconds"> {}

export const IconNMRDataSourceForm = ({
  fieldLabels,
  permissions,
  title,
  subtitle,
  initialValues,
  onSubmit,
  onCancel,
  loading,
  submitButtonLabel,
  event,
}: EntityFormProps<"dataSources">) => {
  const [enabled, setEnabled] = useState(initialValues?.enabled ?? true);
  const [monitoringOnly, setMonitoringOnly] = useState(initialValues?.monitoringOnly ?? false);

  const history = useHistory();
  const bridgeId = new URLSearchParams(history.location.search).get("bridgeId");
  const { data: bridgeDetail } = useEntityDetail<Bridge>(bridgeConstants.resource, bridgeId || 0, undefined, {
    enabled: !initialValues?.bridge && !!bridgeId,
  });

  useEffect(() => {
    setEnabled(initialValues?.enabled ?? true);
  }, [initialValues?.enabled]);

  useEffect(() => {
    setMonitoringOnly(initialValues?.monitoringOnly ?? false);
  }, [initialValues?.monitoringOnly]);

  const DataSourceFormSchema = yup.object().shape({
    bridge: yup.object().required("A bridge is required").typeError("A bridge is required"),
    name: yup.string().required("A name is required").typeError("A name is required"),
    type: yup
      .object()
      .required("A source type is required")
      .typeError("A source type is required")
      .default({ id: "IconNMR", name: "IconNMR" } as { id: DataSourceType; name: DataSourceType }),
    directories: yup
      .array()
      .min(1, "Please select a valid TopSpin directory")
      .required("Please select a valid TopSpin directory")
      .typeError("Please select a valid TopSpin directory"),
  });

  const defaultCutoff = useMemo(() => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    return now;
  }, []);

  const defaultValues: Partial<IconNMRDataSourceFormValues> = useMemo(
    () => ({
      ...initialValues,
      bridge: initialValues?.bridge ?? bridgeDetail ?? undefined,
      type: initialValues?.type
        ? { id: initialValues.type, name: initialValues.type }
        : { id: "IconNMR", name: "IconNMR" },
      directories: initialValues?.directories?.map((d) => ({ id: d, name: d })),
      cutoffDate: initialValues?.cutoffDate
        ? new Date(initialValues.cutoffDate)
        : event === "ADD"
          ? defaultCutoff
          : undefined,
      datasetUpdateMode: initialValues?.datasetUpdateMode
        ? {
            id: initialValues.datasetUpdateMode,
            name: DataSourceUpdateModesTranslator[initialValues.datasetUpdateMode],
          }
        : { id: "Deactivated", name: DataSourceUpdateModesTranslator.Deactivated },
    }),
    [bridgeDetail, defaultCutoff, event, initialValues]
  );

  const {
    handleSubmit,
    control,
    register,
    watch,
    setValue,
    clearErrors,
    reset,
    formState: { errors, isSubmitting, dirtyFields },
    setError,
  } = useForm<Partial<IconNMRDataSourceFormValues>>({
    values: defaultValues,
    resolver: yupResolver(DataSourceFormSchema),
  });

  // show bridge details on select
  const currentBridge = watch("bridge");
  const {
    data: currentBridgeFull,
    status,
    isLoading,
    error,
    fetchStatus,
  } = useEntityDetail<Bridge>(bridgeConstants.resource, currentBridge?.id || 0, {}, { enabled: !!currentBridge?.id });

  const currentFormat = watch("format");
  const { data: currentFormatFull } = useEntityDetail<DataFormat>(
    dataFormatsConstants.resource,
    currentFormat?.id || 0,
    {},
    { enabled: !!currentFormat?.id }
  );

  const currentFileExcludePatterns = watch("fileExcludePatterns");

  const datasetInitialValues: Partial<Dataset> | undefined = useMemo(() => {
    if (!initialValues) return undefined;
    return {
      ...initialValues.datasetTemplate,
      customFields: customValuesListToCustomFieldDict(initialValues.datasetTemplate?.customValues),
    };
  }, [initialValues]);
  const {
    form,
    getValues,
    errors: errorsDatasetForm,
    trigger: triggerDatasetForm,
  } = useDatasetForm({
    initialValues: datasetInitialValues,
  });

  return (
    <>
      <FormHeader title={title} subtitle={subtitle} />
      <form
        onSubmit={(e) => {
          e.preventDefault();
        }}
        autoComplete="off"
        className={`form-horizontal ${styles.form_holder}`}
      >
        <fieldset>
          <legend className="col-md-offset-2 col-md-10">IconNMR monitoring</legend>
        </fieldset>

        <BridgesVirtualizedSelectForm
          id="bridge"
          label={fieldLabels.bridge}
          control={control}
          horizontal
          required
          disabled={!!initialValues?.bridge}
        />

        {currentBridgeFull && (
          <FormFieldLayout id="" label="" horizontal>
            <LoadingWrapper status={status} error={error} fetchStatus={isLoading ? "fetching" : fetchStatus}>
              <BridgeContentRow bridge={currentBridgeFull} />
            </LoadingWrapper>
          </FormFieldLayout>
        )}

        {currentBridge ? (
          <>
            <fieldset>
              <legend className="col-md-offset-2 col-md-10">Configuration</legend>
            </fieldset>

            <InputFormField
              id="name"
              label={fieldLabels.name}
              errors={errors}
              register={register}
              autoFocus={!!initialValues?.bridge}
              required
              placeholder="Insert name..."
            />

            <FormFieldLayout id="" label={fieldLabels.enabled} hasLabel horizontal>
              <div className="flex align-center" style={{ padding: "10px 0" }}>
                <ToggleButtonComponent checked={enabled} setChecked={setEnabled}>
                  <></>
                </ToggleButtonComponent>
              </div>
            </FormFieldLayout>

            <SelectFormField
              id="type"
              label={fieldLabels.type}
              items={DataSourceTypeFormValues.filter((d) => d.id === "IconNMR")}
              required
              control={control}
              errors={errors}
              isClearable={false}
              isDisabled={initialValues?.type !== undefined}
            />

            <DataFormatsVirtualizedSelectForm
              id="format"
              control={control}
              horizontal
              required
              label={fieldLabels.format}
              disabled
            />

            {!!currentFormatFull?.fileExcludePatterns?.length && (
              <SelectFormField<FileExcludePattern>
                id="fileExcludePatterns"
                label={fieldLabels.fileExcludePatterns}
                items={currentFormatFull.fileExcludePatterns}
                control={control}
                isMulti
                getOptionLabel={(option) => option.name}
                getOptionValue={(option) => option.regex}
              />
            )}

            {!!currentFileExcludePatterns?.length && (
              <FormFieldLayout id="excludePatternRegex" label="" horizontal>
                {currentFileExcludePatterns.map((p, index) => (
                  <div className="flex align-center gap-5" style={{ marginBottom: "5px" }} key={index}>
                    <LucideIcon name="corner-down-right" color="var(--gray-400)" style={{ margin: "10px" }} />
                    <input className="form form-control" value={p.regex} readOnly style={{ overflow: "scroll" }} />
                  </div>
                ))}
              </FormFieldLayout>
            )}

            {currentBridgeFull && (
              <FileBrowserFormField<DataSourceFormValues>
                id="directories"
                label="TopSpin directories"
                items={[]}
                required={true}
                control={control}
                bridge={currentBridgeFull}
                setValue={setValue}
                errors={errors}
                clearErrors={clearErrors}
              />
            )}

            <SingleDatePickerFormField
              id="cutoffDate"
              label={fieldLabels.cutoffDate}
              control={control}
              errors={errors}
              horizontal
              children={<CutoffDateInformation />}
              type="datetime"
            />

            {currentBridgeFull?.type === "SFTP" && (
              <FormFieldLayout id="" label={fieldLabels.monitoringOnly} hasLabel horizontal>
                <div className="flex align-center" style={{ padding: "10px 0" }}>
                  <ToggleButtonComponent checked={monitoringOnly} setChecked={setMonitoringOnly}>
                    <></>
                  </ToggleButtonComponent>
                </div>
              </FormFieldLayout>
            )}

            <hr style={{ margin: "20px 0", borderColor: "var(--gray-200)" }} />

            {!monitoringOnly && (
              <>
                <fieldset>
                  <legend className="col-md-offset-2 col-md-10 flex align-center gap-5 no-legend-style">
                    <span style={{ width: "100%" }}>Default dataset values</span>
                    <DatasetTemplateInformation />
                  </legend>
                </fieldset>

                <fieldset>
                  <legend className="col-md-offset-2 col-md-10">Basic dataset</legend>
                </fieldset>

                {form}

                <hr style={{ margin: "20px 0", borderColor: "var(--gray-200)" }} />

                <fieldset>
                  <legend className="col-md-offset-2 col-md-10 flex align-center gap-5">
                    <span style={{ width: "100%" }}>Metadata mapping</span>
                    <MetaDataTemplateInformation />
                  </legend>
                </fieldset>

                <CustomImportsVirtualizedSelectForm
                  id="customImport"
                  label={fieldLabels.customImport}
                  control={control}
                  horizontal
                  placeholder="Select metadata template..."
                />
              </>
            )}

            <div style={{ marginBottom: "40px" }} />
          </>
        ) : (
          <>
            <FormFieldLayout id="" label="" horizontal>
              <hr style={{ margin: "5px 0 15px 0" }} />
              <Alert fit centered type="light" message="Select bridge to continue..." />
            </FormFieldLayout>
          </>
        )}

        <FormButtons
          onClose={onCancel}
          onSubmit={async () => {
            clearErrors("datasetTemplate");
            if (!(await triggerDatasetForm())) {
              setError("datasetTemplate", { message: "Please fill in all required fields", type: "validate" });
            } else
              await handleSubmit(async (entity) => {
                await onSubmit({
                  ...entity,
                  bridge: entity?.bridge as Bridge,
                  type: entity?.type?.id,
                  directories: entity?.directories?.map((d) => d.id),
                  enabled: enabled,
                  intervalInSeconds: 60,
                  cutoffDate: entity?.cutoffDate?.toISOString(),
                  datasetTemplate: !!monitoringOnly ? undefined : (getValues() ?? undefined),
                  datasetUpdateMode: "Deactivated",
                  monitoringOnly: !!monitoringOnly,
                });
              })();
          }}
          loading={loading}
          submitButtonLabel={submitButtonLabel || "Save changes"}
          disabled={isSubmitting}
          errors={{ ...errors, ...errorsDatasetForm }}
          reset={["EDIT", "CLONE"].includes(event) ? () => reset(defaultValues) : undefined}
          dirtyFields={["EDIT", "CLONE"].includes(event) ? Object.keys(dirtyFields).length : undefined}
        />
      </form>
    </>
  );
};
